import React, { useEffect, useState } from 'react';

import { useThemeContext } from 'global/ThemeContext/ThemeContext';

interface ThemeFallbackProps {
  children: React.ReactNode;
}

const ThemeFallback: React.FC<ThemeFallbackProps> = ({ children }) => {
  const theme = useThemeContext();
  const [hasThemeError, setHasThemeError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Verifica se o tema está carregado corretamente
    if (theme) {
      const hasValidColors = theme.primary && theme.background?.primary && theme.text?.primary;

      if (!hasValidColors) {
        console.warn('[ThemeFallback] Tema inválido detectado:', theme);
        setHasThemeError(true);
      } else {
        setHasThemeError(false);
      }
    }
  }, [theme]);

  // Aplica estilos de fallback no body
  useEffect(() => {
    const applyFallbackStyles = () => {
      document.body.style.backgroundColor = '#0A0A0A';
      document.body.style.color = '#E9E9F0';
      document.body.style.fontFamily = 'Roboto, sans-serif';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
    };

    if (hasThemeError || !theme) {
      applyFallbackStyles();
    } else {
      // Remove estilos de fallback quando o tema está ok
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    }

    return () => {
      // Cleanup ao desmontar
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, [hasThemeError, theme]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setHasThemeError(false);
    // Força reload do tema
    window.location.reload();
  };

  if (hasThemeError && retryCount < 3) {
    return (
      <div
        style={{
          backgroundColor: '#0A0A0A',
          color: '#E9E9F0',
          fontFamily: 'Roboto, sans-serif',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#DBB958', marginBottom: '1rem' }}>Problema ao carregar o tema</h2>
        <p style={{ marginBottom: '2rem', maxWidth: '400px' }}>
          Houve um problema ao carregar as configurações visuais da aplicação. Isso pode afetar a
          aparência dos botões e outros elementos.
        </p>
        <button
          onClick={handleRetry}
          style={{
            backgroundColor: '#DBB958',
            color: '#000000',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            fontFamily: 'Roboto, sans-serif',
          }}
        >
          Tentar novamente
        </button>
        <p style={{ marginTop: '1rem', fontSize: '14px', color: '#CECECE' }}>
          Tentativa {retryCount + 1} de 3
        </p>
      </div>
    );
  }

  if (hasThemeError && retryCount >= 3) {
    return (
      <div
        style={{
          backgroundColor: '#0A0A0A',
          color: '#E9E9F0',
          fontFamily: 'Roboto, sans-serif',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#FF0000', marginBottom: '1rem' }}>Erro persistente no tema</h2>
        <p style={{ marginBottom: '2rem', maxWidth: '400px' }}>
          Não foi possível carregar o tema após várias tentativas. A aplicação pode não funcionar
          corretamente.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#FF0000',
            color: '#FFFFFF',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer',
            fontFamily: 'Roboto, sans-serif',
          }}
        >
          Recarregar página
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ThemeFallback;
